import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	Checkbox,
	Heading,
	HStack,
	Icon,
	IconButton,
	Input,
	InputGroup,
	InputLeftElement,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Skeleton,
	Table,
	TableContainer,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	useDisclosure,
	VStack
} from "@chakra-ui/react";
import { RpcError } from "grpc-web";
import { FunctionComponent, ReactElement, useEffect, useMemo, useRef, useState } from "react";
import {
	FaEllipsisV,
	FaEdit,
	FaTrash,
	FaPlus,
	FaSearch,
	FaSortUp,
	FaSortDown,
	FaSort,
	FaFileInvoiceDollar
} from "react-icons/fa";

// Components
import LayoutWrapper from "../../../shared/layout";
import AddRateModal from "./components/AddRateModal";

// Hooks
import { useAuth } from "../../../hooks/useAuth";
import useClaimsToast from "../../../hooks/useToast";

// Utils
import { getProviderRates, deleteProviderRate, upsertProviderRate } from "../../../utils/api/providers";
import { listMatterBillingCodes } from "../../../utils/api/benefits";
import { convertUnixTimestampToLocalDate } from "../../../utils/helpers";
import { GroupSelectOption, SortDirection } from "../../../utils/types";

// Protos
import { GetProviderRatesResponse, ProviderRate } from "../../../utils/api/protos/messages/providers.message_pb";
import { MatterBillingCode } from "../../../utils/api/protos/messages/benefits.message_pb";

interface ProviderProfilePageProps {}

interface RateRow {
	id: number;
	benefitCode: string;
	standardHourlyRate: string;
	standardFlatRate: string;
	updatedAt: string;
}

type SortField = "benefitCode" | "benefitName" | "standardHourlyRate" | "standardFlatRate" | "updatedAt";

/**
 * Provider Profile page - displays provider's rates in a table
 */
const ProviderProfilePage: FunctionComponent<ProviderProfilePageProps> = (): ReactElement => {
	// State
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [rates, setRates] = useState<RateRow[]>([]);
	const [benefitOptions, setBenefitOptions] = useState<GroupSelectOption[]>([]);
	const [benefitCodeToName, setBenefitCodeToName] = useState<Map<string, string>>(new Map());
	const [isLoadingBenefits, setIsLoadingBenefits] = useState<boolean>(false);
	const [editingRate, setEditingRate] = useState<RateRow | null>(null);
	const [deletingRate, setDeletingRate] = useState<RateRow | null>(null);
	const [isDeleting, setIsDeleting] = useState<boolean>(false);

	// Search and sort state
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [sortField, setSortField] = useState<SortField>("benefitCode");
	const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.Asc);

	// Bulk selection state
	const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
	const [isBulkDeleting, setIsBulkDeleting] = useState<boolean>(false);

	// Modal controls
	const { isOpen: isAddModalOpen, onOpen: onAddModalOpen, onClose: onAddModalClose } = useDisclosure();
	const { isOpen: isDeleteDialogOpen, onOpen: onDeleteDialogOpen, onClose: onDeleteDialogClose } = useDisclosure();
	const { isOpen: isBulkDeleteOpen, onOpen: onBulkDeleteOpen, onClose: onBulkDeleteClose } = useDisclosure();
	const cancelRef = useRef<HTMLButtonElement>(null);
	const bulkCancelRef = useRef<HTMLButtonElement>(null);

	// Hooks
	const { getUser } = useAuth();
	const toast = useClaimsToast();
	const user = getUser();

	const resolveBenefitName = (benefitCode: string): string => {
		return benefitCodeToName.get(benefitCode) || benefitCode;
	};

	const ratesWithBenefitNames = useMemo(
		() =>
			rates.map((rate) => ({
				...rate,
				benefitName: resolveBenefitName(rate.benefitCode)
			})),
		[rates, benefitCodeToName]
	);

	// Get list of existing benefit codes for validation
	const existingBenefitCodes = useMemo(() => rates.map((r) => r.benefitCode), [rates]);

	// Filtered and sorted rates
	const filteredAndSortedRates = useMemo(() => {
		let result = [...ratesWithBenefitNames];

		// Filter by search term
		if (searchTerm) {
			const lowerSearch = searchTerm.toLowerCase();
			result = result.filter(
				(rate) =>
					rate.benefitCode.toLowerCase().includes(lowerSearch) || rate.benefitName.toLowerCase().includes(lowerSearch)
			);
		}

		// Sort
		result.sort((a, b) => {
			let aVal: string | number = a[sortField];
			let bVal: string | number = b[sortField];

			// Handle currency sorting
			if (sortField === "standardHourlyRate" || sortField === "standardFlatRate") {
				aVal = parseFloat(aVal as string) || 0;
				bVal = parseFloat(bVal as string) || 0;
			}

			if (aVal < bVal) return sortDirection === SortDirection.Asc ? -1 : 1;
			if (aVal > bVal) return sortDirection === SortDirection.Asc ? 1 : -1;
			return 0;
		});

		return result;
	}, [ratesWithBenefitNames, searchTerm, sortField, sortDirection]);

	// Check if all visible rows are selected
	const allSelected = useMemo(() => {
		if (filteredAndSortedRates.length === 0) return false;
		return filteredAndSortedRates.every((rate) => selectedIds.has(rate.id));
	}, [filteredAndSortedRates, selectedIds]);

	/**
	 * Fetch provider rates and benefits on mount
	 */
	useEffect(() => {
		if (user?.externalId) {
			fetchRates();
		}
		fetchBenefits();
	}, [user?.externalId]);

	const fetchRates = () => {
		if (!user?.externalId) return;

		setIsLoading(true);
		getProviderRates(user.externalId)
			.then((response: GetProviderRatesResponse) => {
				const ratesData: RateRow[] = response.getRatesList().map((rate: ProviderRate) => ({
					id: rate.getId(),
					benefitCode: rate.getBenefitCode(),
					standardHourlyRate: rate.hasStandardHourlyRate() ? rate.getStandardHourlyRate() : "",
					standardFlatRate: rate.hasStandardFlatRate() ? rate.getStandardFlatRate() : "",
					updatedAt: rate.hasUpdatedAt()
						? convertUnixTimestampToLocalDate(rate.getUpdatedAt()).toLocaleDateString()
						: ""
				}));
				setRates(ratesData);
			})
			.catch((err: RpcError) => {
				toast("Error", err.message || "Failed to load rates", "error");
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const fetchBenefits = async () => {
		setIsLoadingBenefits(true);
		try {
			const response = await listMatterBillingCodes();
			const billingCodes = response.getMatterBillingCodesList();

			// Build code to name mapping for ALL codes so existing rates (including any for
			// now-non-billable codes) still resolve their display name.
			const codeNameMap = new Map<string, string>();
			billingCodes.forEach((code: MatterBillingCode) => {
				codeNameMap.set(code.getMatterBillingCode(), code.getMatterTitle());
			});

			// Only offer billable codes in the add-rate dropdown: exclude codes the benefits
			// service flags non-billable (isBillable=false, e.g. member direct-billed HCA codes).
			const codeOptions = billingCodes
				.filter((code: MatterBillingCode) => code.getIsBillable())
				.map((code: MatterBillingCode) => ({
					label: `${code.getMatterBillingCode()} - ${code.getMatterTitle()}`,
					value: code.getMatterBillingCode()
				}));

			setBenefitCodeToName(codeNameMap);

			// Wrap in a single group for the grouped select component
			const options: GroupSelectOption[] = [
				{
					label: "Benefit Codes",
					value: "benefit-codes",
					options: codeOptions
				}
			];

			setBenefitOptions(options);
		} catch (err) {
			console.warn("Failed to fetch billing codes:", err);
		} finally {
			setIsLoadingBenefits(false);
		}
	};

	const formatCurrency = (value: string): string => {
		if (!value) return "-";
		const num = parseFloat(value);
		if (isNaN(num)) return value;
		return num.toLocaleString(undefined, { style: "currency", currency: "USD" });
	};

	const handleSort = (field: SortField) => {
		if (sortField === field) {
			setSortDirection(sortDirection === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc);
		} else {
			setSortField(field);
			setSortDirection(SortDirection.Asc);
		}
	};

	const getSortIcon = (field: SortField) => {
		if (sortField !== field) return FaSort;
		return sortDirection === SortDirection.Asc ? FaSortUp : FaSortDown;
	};

	const handleSelectAll = (checked: boolean) => {
		if (checked) {
			setSelectedIds(new Set(filteredAndSortedRates.map((r) => r.id)));
		} else {
			setSelectedIds(new Set());
		}
	};

	const handleSelectRow = (id: number, checked: boolean) => {
		const newSelected = new Set(selectedIds);
		if (checked) {
			newSelected.add(id);
		} else {
			newSelected.delete(id);
		}
		setSelectedIds(newSelected);
	};

	const handleAddRate = () => {
		setEditingRate(null);
		onAddModalOpen();
	};

	const handleEditRate = (rate: RateRow) => {
		setEditingRate(rate);
		onAddModalOpen();
	};

	const handleDeleteClick = (rate: RateRow) => {
		setDeletingRate(rate);
		onDeleteDialogOpen();
	};

	const handleSaveRate = async (benefitCode: string, hourlyRate: string, flatRate: string) => {
		if (!user?.externalId || !user?.id) return;

		const isEditing = !!editingRate;

		try {
			await upsertProviderRate(user.externalId, benefitCode, hourlyRate || undefined, flatRate || undefined);

			toast("Success", isEditing ? "Rate updated successfully" : "Rate added successfully", "success");
			fetchRates();
		} catch (err: unknown) {
			const error = err as RpcError;
			toast("Error", error.message || "Failed to save rate", "error");
			throw err;
		}
	};

	const handleConfirmDelete = async () => {
		if (!deletingRate || !user?.id) return;

		setIsDeleting(true);
		try {
			await deleteProviderRate(deletingRate.id);

			toast("Success", "Rate removed successfully", "success");
			onDeleteDialogClose();
			setDeletingRate(null);
			setSelectedIds((prev) => {
				const newSet = new Set(prev);
				newSet.delete(deletingRate.id);
				return newSet;
			});
			fetchRates();
		} catch (err: unknown) {
			const error = err as RpcError;
			toast("Error", error.message || "Failed to delete rate", "error");
		} finally {
			setIsDeleting(false);
		}
	};

	const handleBulkDelete = async () => {
		if (selectedIds.size === 0 || !user?.id) return;

		setIsBulkDeleting(true);
		const idsToDelete = Array.from(selectedIds);
		let successCount = 0;
		let failCount = 0;

		for (const id of idsToDelete) {
			const rate = rates.find((r) => r.id === id);
			if (!rate) continue;

			try {
				await deleteProviderRate(id);
				successCount++;
			} catch {
				failCount++;
			}
		}

		if (successCount > 0) {
			toast("Success", `${successCount} rate${successCount !== 1 ? "s" : ""} removed successfully`, "success");
		}
		if (failCount > 0) {
			toast("Error", `Failed to remove ${failCount} rate${failCount !== 1 ? "s" : ""}`, "error");
		}

		setSelectedIds(new Set());
		onBulkDeleteClose();
		fetchRates();
		setIsBulkDeleting(false);
	};

	const handleCloseAddModal = () => {
		onAddModalClose();
		setEditingRate(null);
	};

	// Loading skeleton rows
	const renderSkeletonRows = () => (
		<>
			{[1, 2, 3, 4, 5].map((i) => (
				<Tr key={`skeleton-${i}`}>
					<Td>
						<Skeleton height="16px" width="16px" />
					</Td>
					<Td>
						<Skeleton height="16px" width="80px" />
					</Td>
					<Td>
						<Skeleton height="16px" width="150px" />
					</Td>
					<Td>
						<Skeleton height="16px" width="70px" />
					</Td>
					<Td>
						<Skeleton height="16px" width="70px" />
					</Td>
					<Td>
						<Skeleton height="16px" width="80px" />
					</Td>
					<Td>
						<Skeleton height="16px" width="24px" />
					</Td>
				</Tr>
			))}
		</>
	);

	// Empty state
	const renderEmptyState = () => (
		<Box px={6} py={12} textAlign="center">
			<Icon as={FaFileInvoiceDollar} boxSize={12} color="gray.300" mb={4} />
			<Text color="gray.600" fontSize="lg" fontWeight="500" mb={2}>
				No rates configured yet
			</Text>
			<Text color="gray.500" fontSize="sm" mb={6}>
				Set up your standard rates to streamline claim submissions
			</Text>
			<Button leftIcon={<FaPlus />} colorScheme="orange" size="sm" onClick={handleAddRate}>
				Add Your First Rate
			</Button>
		</Box>
	);

	// No results state
	const renderNoResults = () => (
		<Box px={6} py={8} textAlign="center">
			<Icon as={FaSearch} boxSize={8} color="gray.300" mb={3} />
			<Text color="gray.500">No rates match your search</Text>
		</Box>
	);

	return (
		<LayoutWrapper>
			<Box mx={10} mt={10} mb={10}>
				<VStack align="stretch" spacing={6}>
					{/* Header */}
					<HStack justifyContent="space-between" alignItems="center">
						<Heading as="h1" size="lg" color="gray.700">
							My Rates
						</Heading>
						<HStack spacing={3}>
							{selectedIds.size > 0 && (
								<Button leftIcon={<FaTrash />} colorScheme="red" variant="outline" size="sm" onClick={onBulkDeleteOpen}>
									Delete ({selectedIds.size})
								</Button>
							)}
							<Button leftIcon={<FaPlus />} colorScheme="orange" size="sm" onClick={handleAddRate}>
								Add Rate
							</Button>
						</HStack>
					</HStack>

					{/* Search */}
					{!isLoading && rates.length > 0 && (
						<InputGroup size="sm" maxW="300px">
							<InputLeftElement pointerEvents="none">
								<FaSearch color="gray" />
							</InputLeftElement>
							<Input
								placeholder="Search by code or name..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								borderRadius="md"
							/>
						</InputGroup>
					)}

					{/* Table Container */}
					<Box bg="white" borderRadius="md" boxShadow="sm" border="1px solid" borderColor="gray.200" overflow="hidden">
						<TableContainer>
							<Table size="sm" variant="simple">
								<Thead bg="gray.50">
									<Tr>
										<Th width="40px" borderBottom="1px solid" borderColor="gray.200">
											<Checkbox
												isChecked={allSelected && filteredAndSortedRates.length > 0}
												isIndeterminate={selectedIds.size > 0 && !allSelected}
												onChange={(e) => handleSelectAll(e.target.checked)}
												isDisabled={isLoading || filteredAndSortedRates.length === 0}
												colorScheme="orange"
											/>
										</Th>
										<Th
											cursor="pointer"
											onClick={() => handleSort("benefitCode")}
											borderBottom="1px solid"
											borderColor="gray.200"
											_hover={{ bg: "gray.100" }}>
											<HStack spacing={1}>
												<Text>Code</Text>
												<Icon
													as={getSortIcon("benefitCode")}
													color={sortField === "benefitCode" ? "orange.500" : "gray.400"}
												/>
											</HStack>
										</Th>
										<Th
											cursor="pointer"
											onClick={() => handleSort("benefitName")}
											borderBottom="1px solid"
											borderColor="gray.200"
											_hover={{ bg: "gray.100" }}>
											<HStack spacing={1}>
												<Text>Name</Text>
												<Icon
													as={getSortIcon("benefitName")}
													color={sortField === "benefitName" ? "orange.500" : "gray.400"}
												/>
											</HStack>
										</Th>
										<Th
											cursor="pointer"
											onClick={() => handleSort("standardHourlyRate")}
											borderBottom="1px solid"
											borderColor="gray.200"
											isNumeric
											_hover={{ bg: "gray.100" }}>
											<HStack spacing={1} justifyContent="flex-end">
												<Text>Hourly Rate</Text>
												<Icon
													as={getSortIcon("standardHourlyRate")}
													color={sortField === "standardHourlyRate" ? "orange.500" : "gray.400"}
												/>
											</HStack>
										</Th>
										<Th
											cursor="pointer"
											onClick={() => handleSort("standardFlatRate")}
											borderBottom="1px solid"
											borderColor="gray.200"
											isNumeric
											_hover={{ bg: "gray.100" }}>
											<HStack spacing={1} justifyContent="flex-end">
												<Text>Flat Rate</Text>
												<Icon
													as={getSortIcon("standardFlatRate")}
													color={sortField === "standardFlatRate" ? "orange.500" : "gray.400"}
												/>
											</HStack>
										</Th>
										<Th
											cursor="pointer"
											onClick={() => handleSort("updatedAt")}
											borderBottom="1px solid"
											borderColor="gray.200"
											_hover={{ bg: "gray.100" }}>
											<HStack spacing={1}>
												<Text>Last Updated</Text>
												<Icon
													as={getSortIcon("updatedAt")}
													color={sortField === "updatedAt" ? "orange.500" : "gray.400"}
												/>
											</HStack>
										</Th>
										<Th width="50px" borderBottom="1px solid" borderColor="gray.200" />
									</Tr>
								</Thead>
								<Tbody>
									{isLoading && renderSkeletonRows()}
									{!isLoading && rates.length === 0 && (
										<Tr>
											<Td colSpan={7} p={0}>
												{renderEmptyState()}
											</Td>
										</Tr>
									)}
									{!isLoading && rates.length > 0 && filteredAndSortedRates.length === 0 && (
										<Tr>
											<Td colSpan={7} p={0}>
												{renderNoResults()}
											</Td>
										</Tr>
									)}
									{!isLoading &&
										filteredAndSortedRates.map((rate) => (
											<Tr
												key={rate.id}
												_hover={{ bg: "gray.50" }}
												bg={selectedIds.has(rate.id) ? "orange.50" : undefined}>
												<Td>
													<Checkbox
														isChecked={selectedIds.has(rate.id)}
														onChange={(e) => handleSelectRow(rate.id, e.target.checked)}
														colorScheme="orange"
													/>
												</Td>
												<Td fontWeight="500" color="gray.700">
													{rate.benefitCode}
												</Td>
												<Td color="gray.600" maxW="250px" isTruncated title={rate.benefitName}>
													{rate.benefitName}
												</Td>
												<Td isNumeric color="gray.700">
													{formatCurrency(rate.standardHourlyRate)}
												</Td>
												<Td isNumeric color="gray.700">
													{formatCurrency(rate.standardFlatRate)}
												</Td>
												<Td color="gray.500">{rate.updatedAt || "-"}</Td>
												<Td>
													<Menu>
														<MenuButton
															as={IconButton}
															icon={<FaEllipsisV />}
															variant="ghost"
															size="sm"
															aria-label="Actions"
															color="gray.500"
															_hover={{ color: "orange.500" }}
														/>
														<MenuList minW="120px">
															<MenuItem icon={<FaEdit />} onClick={() => handleEditRate(rate)} fontSize="sm">
																Edit
															</MenuItem>
															<MenuItem
																icon={<FaTrash />}
																onClick={() => handleDeleteClick(rate)}
																fontSize="sm"
																color="red.500">
																Delete
															</MenuItem>
														</MenuList>
													</Menu>
												</Td>
											</Tr>
										))}
								</Tbody>
							</Table>
						</TableContainer>
					</Box>

					{/* Footer Info */}
					{!isLoading && rates.length > 0 && (
						<Text fontSize="sm" color="gray.500">
							{searchTerm
								? `Showing ${filteredAndSortedRates.length} of ${rates.length} rate${rates.length !== 1 ? "s" : ""}`
								: `Showing ${rates.length} rate${rates.length !== 1 ? "s" : ""}`}
						</Text>
					)}
				</VStack>
			</Box>

			{/* Add/Edit Rate Modal */}
			<AddRateModal
				isOpen={isAddModalOpen}
				onClose={handleCloseAddModal}
				onSave={handleSaveRate}
				existingBenefitCodes={existingBenefitCodes}
				benefitOptions={benefitOptions}
				isLoadingBenefits={isLoadingBenefits}
				editingRate={
					editingRate
						? {
								benefitCode: editingRate.benefitCode,
								standardHourlyRate: editingRate.standardHourlyRate,
								standardFlatRate: editingRate.standardFlatRate
							}
						: undefined
				}
			/>

			{/* Delete Confirmation Dialog */}
			<AlertDialog isOpen={isDeleteDialogOpen} leastDestructiveRef={cancelRef} onClose={onDeleteDialogClose} isCentered>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							Delete Rate
						</AlertDialogHeader>

						<AlertDialogBody>
							Are you sure you want to delete the rate for{" "}
							<Text as="span" fontWeight="bold">
								{deletingRate?.benefitCode}
							</Text>
							? This action cannot be undone.
						</AlertDialogBody>

						<AlertDialogFooter>
							<Button ref={cancelRef} onClick={onDeleteDialogClose} size="sm">
								Cancel
							</Button>
							<Button colorScheme="red" onClick={handleConfirmDelete} ml={3} size="sm" isLoading={isDeleting}>
								Delete
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>

			{/* Bulk Delete Confirmation Dialog */}
			<AlertDialog isOpen={isBulkDeleteOpen} leastDestructiveRef={bulkCancelRef} onClose={onBulkDeleteClose} isCentered>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							Delete {selectedIds.size} Rate{selectedIds.size !== 1 ? "s" : ""}
						</AlertDialogHeader>

						<AlertDialogBody>
							Are you sure you want to delete{" "}
							<Text as="span" fontWeight="bold">
								{selectedIds.size} rate{selectedIds.size !== 1 ? "s" : ""}
							</Text>
							? This action cannot be undone.
						</AlertDialogBody>

						<AlertDialogFooter>
							<Button ref={bulkCancelRef} onClick={onBulkDeleteClose} size="sm">
								Cancel
							</Button>
							<Button colorScheme="red" onClick={handleBulkDelete} ml={3} size="sm" isLoading={isBulkDeleting}>
								Delete All
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</LayoutWrapper>
	);
};

export default ProviderProfilePage;
